<template>
  <transition name="modal-fade">
    <div v-if="show" class="fixed inset-0 z-50 overflow-hidden">
      <!-- Overlay -->
      <div
        class="absolute inset-0 bg-black/40 transition-opacity duration-300"
        @click="handleBackdropClick"
      />

      <!-- Modal Container with proper centering -->
      <div class="flex items-center justify-center min-h-screen p-4">
        <div
          :class="modalClasses"
          @click.stop
          role="dialog"
          aria-modal="true"
          :aria-labelledby="title ? 'modal-title' : undefined"
        >

          <!-- Modal Header - Responsive -->
          <div v-if="$slots.header || title" class="relative z-10 flex items-center justify-between mb-4 sm:mb-6">
            <div class="flex items-center space-x-2 sm:space-x-3">
              <!-- Optional Icon Slot -->
              <div v-if="$slots.icon" class="modal-icon w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center flex-shrink-0">
                <slot name="icon" />
              </div>

              <!-- Title - Responsive Typography -->
              <h2 v-if="title" id="modal-title" class="text-xl sm:text-2xl font-bold text-gray-900">
                {{ title }}
              </h2>

              <!-- Custom Header Slot -->
              <slot name="header" />
            </div>

            <!-- Close Button - flat rectangular style -->
            <button
              v-if="showClose"
              @click="close"
              class="px-3 py-1.5 rounded-lg border border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors duration-200 flex-shrink-0"
              aria-label="Close modal"
            >
              Close
            </button>
          </div>

          <!-- Modal Body -->
          <div class="relative z-10">
            <slot />
          </div>

          <!-- Modal Footer - Responsive -->
          <div v-if="$slots.footer" class="relative z-10 mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-gray-200">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const modalVariants = cva(
  // Base styles - flat design with clean border
  'relative bg-white rounded-2xl border border-gray-300 w-full mx-4 transition-colors duration-200',
  {
    variants: {
      size: {
        sm: 'max-w-sm p-6 sm:p-8',
        md: 'max-w-md p-6 sm:p-10',
        lg: 'max-w-2xl p-6 sm:p-10',
        xl: 'max-w-4xl p-8 sm:p-12',
        full: 'max-w-7xl p-8 sm:p-12'
      }
    },
    defaultVariants: {
      size: 'md'
    }
  }
)

export default {
  name: 'Modal',
  props: {
    show: {
      type: Boolean,
      required: true
    },
    size: {
      type: String,
      default: 'md',
      validator: value => ['sm', 'md', 'lg', 'xl', 'full'].includes(value)
    },
    title: {
      type: String,
      default: null
    },
    showClose: {
      type: Boolean,
      default: true
    },
    closeOnBackdrop: {
      type: Boolean,
      default: true
    },
    closeOnEscape: {
      type: Boolean,
      default: true
    },
    customClass: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'update:show'],
  computed: {
    modalClasses() {
      return cn(
        modalVariants({ size: this.size }),
        this.customClass
      )
    }
  },
  watch: {
    show(newValue) {
      if (newValue) {
        this.lockBodyScroll()
        if (this.closeOnEscape) {
          document.addEventListener('keydown', this.handleEscape)
        }
      } else {
        this.unlockBodyScroll()
        if (this.closeOnEscape) {
          document.removeEventListener('keydown', this.handleEscape)
        }
      }
    }
  },
  methods: {
    close() {
      this.$emit('close')
      this.$emit('update:show', false)
    },
    handleBackdropClick() {
      if (this.closeOnBackdrop) {
        this.close()
      }
    },
    handleEscape(event) {
      if (event.key === 'Escape' && this.show) {
        this.close()
      }
    },
    lockBodyScroll() {
      document.body.style.overflow = 'hidden'
    },
    unlockBodyScroll() {
      document.body.style.overflow = ''
    }
  },
  mounted() {
    if (this.show) {
      this.lockBodyScroll()
      if (this.closeOnEscape) {
        document.addEventListener('keydown', this.handleEscape)
      }
    }
  },
  beforeDestroy() {
    this.unlockBodyScroll()
    if (this.closeOnEscape) {
      document.removeEventListener('keydown', this.handleEscape)
    }
  }
}
</script>

<style scoped>
/* Modal fade transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .relative,
.modal-fade-leave-active .relative {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-fade-enter-from .relative,
.modal-fade-leave-to .relative {
  transform: scale(0.95);
  opacity: 0;
}

/* Modal icon - flat solid color */
.modal-icon {
  background-color: var(--button-color, #035891);
  color: white;
}
</style>
